import { TimelineParty } from './enums/timeline-party.enum';

// Values are pinned explicitly and must byte-match the C# enum
// (`VetValue.MarketplaceHub/Modules/Timelines/Enums/TimelineActionType.cs`) —
// the API serializes this enum as a number.
export enum TimelineActionType {
    SellerDataAndDocumentRequest = 0,
    SellerEngagementDocument = 1,
    SellerTeaserApproval = 2,
    SellerCustomProcessDuration = 3,
    SellerCustomSelectDataNdaTerms = 4,
    SellerCustomSelectDueDiligenceNdaTerms = 5,
    SellerCustomMeetingAvailability = 6,
    SellerBuyerSelectionIdNda = 7,
    SellerBuyerSelectionStage2Nda = 8,
    SellerBuyerSelectionDetailedFinancialModel = 9,
    SellerBuyerSelectionMeetings = 10,
    SellerBuyerSelectionStage3Nda = 11,
    BuyerDocumentExecution = 12,
    BuyerExpressionOfInterest = 13,
    BuyerExpressionOfInterestWithRange = 14,
    BuyerReviewDocument = 15,
    BuyerUploadDocument = 16,
    BuyerScheduleMeeting = 17,
    BuyerLoiInformation = 18,
    VetValueBuyerSelectionAndTeaserPosting = 19,
    SellerProvideMeetingAvailability = 20,
    // Display name "Meetings Period" — combines the former "agree on meeting
    // times" step and the former separate "meeting period" step into one event.
    SellerBuyerMeetings = 21,
    // 22 retired — was the separate "meeting period" action type; value AND
    // identifier are never reused so legacy call sites fail compilation
    // instead of silently retargeting.
    SellerProfile = 23,
    BuyerLoiSubmission = 24,
    VetValueLoiFormCreation = 25,
    BuyerLoiFormReview = 26,
    SellerLoiComparison = 27,
    SellerCustomSelectLoiAndDdNdaTerms = 28,
    BuyerReviewVendorContracts = 29,
    SellerBuyerAgreeOnClosingDate = 30,
}

export enum BuyerActionTypeGroups {
    DocumentExecution,
    ExpresstionOfInterest,
    ReviewDocument,
    UploadDocument,
    Custom,
    LoiSubmission,
    LoiFormReview,
    ReviewVendorContracts,
}

export enum SellerActionTypeGroups {
    DataAndDocumentRequest,
    EngagementDocument,
    BuyerSelection,
    Custom,
    NdaTermsSelection,
    TeaserApproval,
    ProvideMeetingAvailability,
    SellerProfile,
    LoiComparison,
}

export enum VetValueActionTypeGroups {
    BuyerSelectionAndTeaserPosting,
    LoiFormCreation,
}

export enum SellerAndBuyerActionTypeGroups {
    BuyerSelection = 0,
    NdaTermsSelection = 1,
    // Fresh identifier replacing the retired "agree on meeting times" group; display "Meetings Period".
    Meetings = 2,
    // 3 retired — was MeetingsPeriod; value AND identifier are never reused.
    LoiAndDdNdaExecution = 4,
    AgreeOnClosingDate = 5,
}

export interface ActionTypeDefinition {
    id: TimelineActionType;
    party: TimelineParty;
    group:
        | BuyerActionTypeGroups
        | SellerActionTypeGroups
        | VetValueActionTypeGroups
        | SellerAndBuyerActionTypeGroups;
    name: string;
    oppositePartyName?: string;
}

export const MarketplaceEventActionTypeList: ActionTypeDefinition[] = [
    {
        id: TimelineActionType.SellerDataAndDocumentRequest,
        party: TimelineParty.Seller,
        group: SellerActionTypeGroups.DataAndDocumentRequest,
        name: 'Data & Docs Request',
    },
    {
        id: TimelineActionType.SellerEngagementDocument,
        party: TimelineParty.Seller,
        group: SellerActionTypeGroups.EngagementDocument,
        name: 'Engagement Document',
    },
    {
        id: TimelineActionType.SellerTeaserApproval,
        party: TimelineParty.Seller,
        group: SellerActionTypeGroups.TeaserApproval,
        name: 'Teaser Approval',
    },
    {
        id: TimelineActionType.SellerCustomProcessDuration,
        party: TimelineParty.Seller,
        group: SellerActionTypeGroups.Custom,
        name: 'Process Duration',
    },
    {
        id: TimelineActionType.SellerCustomSelectDueDiligenceNdaTerms,
        party: TimelineParty.Seller,
        group: SellerActionTypeGroups.NdaTermsSelection,
        name: 'Select Stage Due Diligence NDA Terms',
    },
    {
        id: TimelineActionType.SellerCustomMeetingAvailability,
        party: TimelineParty.Seller,
        group: SellerActionTypeGroups.Custom,
        name: 'Metting Availability',
    },
    {
        id: TimelineActionType.SellerBuyerSelectionStage2Nda,
        party: TimelineParty.Seller,
        group: SellerActionTypeGroups.BuyerSelection,
        name: 'Buyer Selection Stage 2 NDA',
    },
    {
        id: TimelineActionType.SellerBuyerSelectionDetailedFinancialModel,
        party: TimelineParty.SellerAndBuyer,
        group: SellerAndBuyerActionTypeGroups.BuyerSelection,
        name: 'Buyer Selection Detailed Financial Model',
    },
    {
        id: TimelineActionType.SellerBuyerSelectionMeetings,
        party: TimelineParty.Seller,
        group: SellerActionTypeGroups.BuyerSelection,
        name: 'Buyer Selection Meetings',
    },
    {
        id: TimelineActionType.SellerBuyerSelectionStage3Nda,
        party: TimelineParty.Seller,
        group: SellerActionTypeGroups.BuyerSelection,
        name: 'Buyer Selection Stage 3 NDA',
    },
    {
        id: TimelineActionType.BuyerDocumentExecution,
        party: TimelineParty.Buyer,
        group: BuyerActionTypeGroups.DocumentExecution,
        name: 'Document Execution',
    },
    {
        id: TimelineActionType.BuyerExpressionOfInterest,
        party: TimelineParty.Buyer,
        group: BuyerActionTypeGroups.ExpresstionOfInterest,
        name: 'Expression of Interest',
    },
    {
        id: TimelineActionType.BuyerExpressionOfInterestWithRange,
        party: TimelineParty.Buyer,
        group: BuyerActionTypeGroups.ExpresstionOfInterest,
        name: 'Expression of Interest with Range',
    },
    {
        id: TimelineActionType.BuyerReviewDocument,
        party: TimelineParty.Buyer,
        group: BuyerActionTypeGroups.ReviewDocument,
        name: 'Review Document',
    },
    {
        id: TimelineActionType.BuyerUploadDocument,
        party: TimelineParty.Buyer,
        group: BuyerActionTypeGroups.UploadDocument,
        name: 'Upload Document',
    },
    {
        id: TimelineActionType.BuyerScheduleMeeting,
        party: TimelineParty.Buyer,
        group: BuyerActionTypeGroups.Custom,
        name: 'Schedule Meeting',
    },
    {
        id: TimelineActionType.BuyerLoiInformation,
        party: TimelineParty.Buyer,
        group: BuyerActionTypeGroups.Custom,
        name: 'LOI Information',
    },
    {
        id: TimelineActionType.BuyerLoiSubmission,
        party: TimelineParty.Buyer,
        group: BuyerActionTypeGroups.LoiSubmission,
        name: 'Buyer Submits LOI Package',
    },
    {
        id: TimelineActionType.VetValueLoiFormCreation,
        party: TimelineParty.VetValue,
        group: VetValueActionTypeGroups.LoiFormCreation,
        name: 'VetValue Creates LOI Form',
    },
    {
        id: TimelineActionType.BuyerLoiFormReview,
        party: TimelineParty.Buyer,
        group: BuyerActionTypeGroups.LoiFormReview,
        name: 'Buyer Reviews LOI Form',
    },
    {
        id: TimelineActionType.VetValueBuyerSelectionAndTeaserPosting,
        party: TimelineParty.VetValue,
        group: VetValueActionTypeGroups.BuyerSelectionAndTeaserPosting,
        name: 'Buyer Selection / Teaser Posting',
    },
    {
        id: TimelineActionType.SellerBuyerSelectionIdNda,
        party: TimelineParty.SellerAndBuyer,
        group: SellerAndBuyerActionTypeGroups.BuyerSelection,
        name: 'Buyer Selection ID NDA',
        oppositePartyName: 'Execute Document',
    },
    {
        id: TimelineActionType.SellerCustomSelectDataNdaTerms,
        party: TimelineParty.SellerAndBuyer,
        group: SellerAndBuyerActionTypeGroups.NdaTermsSelection,
        name: 'Data NDA Terms Selection',
    },
    {
        id: TimelineActionType.SellerProvideMeetingAvailability,
        party: TimelineParty.SellerAndBuyer,
        group: SellerActionTypeGroups.ProvideMeetingAvailability,
        name: 'Provide Meeting Availability',
    },
    {
        id: TimelineActionType.SellerProfile,
        party: TimelineParty.Seller,
        group: SellerActionTypeGroups.SellerProfile,
        name: 'Seller Profile',
    },
    {
        id: TimelineActionType.SellerBuyerMeetings,
        party: TimelineParty.SellerAndBuyer,
        group: SellerAndBuyerActionTypeGroups.Meetings,
        name: 'Meetings Period',
    },
    {
        id: TimelineActionType.SellerLoiComparison,
        party: TimelineParty.Seller,
        group: SellerActionTypeGroups.LoiComparison,
        name: 'LOI Comparison',
    },
    {
        id: TimelineActionType.SellerCustomSelectLoiAndDdNdaTerms,
        party: TimelineParty.SellerAndBuyer,
        group: SellerAndBuyerActionTypeGroups.LoiAndDdNdaExecution,
        name: 'LOI and DD NDA Execution',
    },
    {
        id: TimelineActionType.BuyerReviewVendorContracts,
        party: TimelineParty.Buyer,
        group: BuyerActionTypeGroups.ReviewVendorContracts,
        name: 'Seller Obligation Contracts Review',
    },
    {
        id: TimelineActionType.SellerBuyerAgreeOnClosingDate,
        party: TimelineParty.SellerAndBuyer,
        group: SellerAndBuyerActionTypeGroups.AgreeOnClosingDate,
        name: 'Agree on Closing Date',
    },
];

export const BuyerLinkActionGroupOptions = [
    {
        id: BuyerActionTypeGroups.DocumentExecution,
        party: TimelineParty.Buyer,
        name: 'Document Execution',
        isImplemented: true,
    },
    {
        id: BuyerActionTypeGroups.ExpresstionOfInterest,
        party: TimelineParty.Buyer,
        name: 'Expression of Interest',
        isImplemented: true,
    },
    {
        id: BuyerActionTypeGroups.ReviewDocument,
        party: TimelineParty.Buyer,
        name: 'Review Document',
        isImplemented: true,
    },
    {
        id: BuyerActionTypeGroups.UploadDocument,
        party: TimelineParty.Buyer,
        name: 'Upload Information/Document',
        isImplemented: true,
    },
    {
        id: BuyerActionTypeGroups.Custom,
        party: TimelineParty.Buyer,
        name: 'Custom',
        isImplemented: false,
    },
    {
        id: BuyerActionTypeGroups.LoiSubmission,
        party: TimelineParty.Buyer,
        name: 'Buyer Submits LOI Package',
        isImplemented: true,
    },
    {
        id: BuyerActionTypeGroups.LoiFormReview,
        party: TimelineParty.Buyer,
        name: 'Buyer Reviews LOI Form',
        isImplemented: true,
    },
    {
        id: BuyerActionTypeGroups.ReviewVendorContracts,
        party: TimelineParty.Buyer,
        name: 'Seller Obligation Contracts Review',
        isImplemented: true,
    },
];

export const SellerLinkActionGroupOptions = [
    {
        id: SellerActionTypeGroups.DataAndDocumentRequest,
        party: TimelineParty.Seller,
        name: 'Data & Document Request',
        isImplemented: true,
    },
    {
        id: SellerActionTypeGroups.EngagementDocument,
        party: TimelineParty.Seller,
        name: 'Engagement Document',
        isImplemented: true,
    },
    // {
    //     id: SellerActionTypeGroups.BuyerSelection,
    //     party: TimelineParty.Seller,
    //     name: 'Buyer Selection',
    //     isImplemented: true,
    // },
    {
        id: SellerActionTypeGroups.TeaserApproval,
        party: TimelineParty.Seller,
        name: 'Teaser Approval',
        isImplemented: true,
    },
    {
        id: SellerActionTypeGroups.Custom,
        party: TimelineParty.Seller,
        name: 'Custom',
        isImplemented: false,
    },
    {
        id: SellerActionTypeGroups.ProvideMeetingAvailability,
        party: TimelineParty.SellerAndBuyer,
        name: 'Provide Meeting Availability',
        isImplemented: true,
    },
    {
        id: SellerActionTypeGroups.SellerProfile,
        party: TimelineParty.Seller,
        name: 'Seller Profile',
        isImplemented: true,
    },
    {
        id: SellerActionTypeGroups.LoiComparison,
        party: TimelineParty.Seller,
        name: 'LOI Comparison',
        isImplemented: true,
    },
];

export const VetValueLinkActionGroupOptions = [
    {
        id: VetValueActionTypeGroups.BuyerSelectionAndTeaserPosting,
        party: TimelineParty.VetValue,
        name: 'Buyer Selection / Teaser Posting',
        isImplemented: true,
    },
    {
        id: VetValueActionTypeGroups.LoiFormCreation,
        party: TimelineParty.VetValue,
        name: 'VetValue Creates LOI Form',
        isImplemented: true,
    },
];

export const SellerAndBuyerLinkActionGroupOptions = [
    {
        id: SellerAndBuyerActionTypeGroups.BuyerSelection,
        party: TimelineParty.SellerAndBuyer,
        name: 'Buyer Selection',
        isImplemented: true,
    },
    {
        id: SellerAndBuyerActionTypeGroups.NdaTermsSelection,
        party: TimelineParty.SellerAndBuyer,
        name: 'NDA Terms Selection',
        isImplemented: true,
    },
    {
        id: SellerAndBuyerActionTypeGroups.LoiAndDdNdaExecution,
        party: TimelineParty.SellerAndBuyer,
        name: 'LOI and DD NDA Execution',
        isImplemented: true,
    },
    {
        id: SellerAndBuyerActionTypeGroups.Meetings,
        party: TimelineParty.SellerAndBuyer,
        name: 'Meetings Period',
        isImplemented: true,
    },
    {
        id: SellerAndBuyerActionTypeGroups.AgreeOnClosingDate,
        party: TimelineParty.SellerAndBuyer,
        name: 'Agree on Closing Date',
        isImplemented: true,
    },
];

export const BuyerExpressionOfInterestOptionsList = [
    {
        label: 'No need to specify range',
        value: TimelineActionType.BuyerExpressionOfInterest,
    },
    {
        label: 'Indicate value range',
        value: TimelineActionType.BuyerExpressionOfInterestWithRange,
    },
];

export const BuyerSelectionOptionsList = [
    {
        label: 'ID NDA',
        value: TimelineActionType.SellerBuyerSelectionIdNda,
    },
    /*  {
        label: 'Data NDA',
        value: TimelineActionType.SellerBuyerSelectionStage2Nda,
    },*/
    {
        label: 'Detailed Financial Model',
        value: TimelineActionType.SellerBuyerSelectionDetailedFinancialModel,
    } /*
    {
        label: 'Meetings',
        value: TimelineActionType.SellerBuyerSelectionMeetings,
    },
    {
        label: 'Due Diligence NDA',
        value: TimelineActionType.SellerBuyerSelectionStage3Nda,
    }*/,
];

export const NdaTermSelectionOptionsList = [
    {
        label: 'Data NDA Terms',
        value: TimelineActionType.SellerCustomSelectDataNdaTerms,
    },
];

export const BuyerDocumentExecutionOptionsList = [
    {
        label: 'Document Execution',
        value: TimelineActionType.BuyerDocumentExecution,
    },
    {
        label: 'Upload Document',
        value: TimelineActionType.BuyerUploadDocument,
    },
    {
        label: 'Review Document',
        value: TimelineActionType.BuyerReviewDocument,
    },
];

export const ActionsWithBuyerDocumentToSign: TimelineActionType[] = [
    TimelineActionType.SellerBuyerSelectionIdNda,
    TimelineActionType.SellerCustomSelectDataNdaTerms,
    TimelineActionType.SellerCustomSelectLoiAndDdNdaTerms,
    TimelineActionType.BuyerDocumentExecution,
];

export function getActionTypeName(actionType: TimelineActionType): string {
    return MarketplaceEventActionTypeList.find((a) => a.id === actionType)?.name ?? '';
}
